// NoFearNShyHome.jsx
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Bar, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPen,
  faMicrophone,
  faPalette,
  faLeaf,
  faStar,
} from "@fortawesome/free-solid-svg-icons";
import { motion, AnimatePresence } from "framer-motion";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);

const NoFearNShyHome = () => {
  const [stakeholder, setStakeholder] = useState("child");
  const [activeTab, setActiveTab] = useState("home");

  // -------------------- Child State --------------------
  const [selectedMicroStep, setSelectedMicroStep] = useState("");
  const [treeLeaves, setTreeLeaves] = useState(12);
  const [streakDays, setStreakDays] = useState(5);
  const [badges, setBadges] = useState(["First Reflection", "3 Micro-Steps Completed"]);
  const [newBadge, setNewBadge] = useState(""); // for animation

  // For Reflection tab
  const [reflectionText, setReflectionText] = useState(
    "I don’t speak in class because I fear mistakes"
  );

  // For Settings tab
  const [settings, setSettings] = useState({
    notifications: true,
    darkMode: true,
    voiceFeedback: true,
  });

  const [mode, setMode] = useState(""); // "write", "voice", "draw"
  const [activeMode, setActiveMode] = useState(""); 
  const [completedActions, setCompletedActions] = useState({
    write: false,
    voice: false,
    draw: false,
  });

  const handleCompleteAction = (mode) => {
    if (!completedActions[mode]) {
      setCompletedActions((prev) => ({ ...prev, [mode]: true }));
      setTreeLeaves((prev) => prev + 1);
      setStreakDays((prev) => prev + 1);

      const badgeName = `${mode.charAt(0).toUpperCase() + mode.slice(1)} Master`;
      if (!badges.includes(badgeName)) {
        setBadges([...badges, badgeName]);
        setNewBadge(badgeName);
        setTimeout(() => setNewBadge(""), 3000);
      }
    }
    setActiveMode(""); // Close panel after completion
  };

  // -------------------- Dummy Data --------------------
  const dummyData = {
    child: {
      name: "Ivaan",
      dailyReflection: "I don’t speak in class because I fear mistakes",
      microSteps: [
        "Write answer first",
        "Practice with parent",
        "Share with friend optionally",
      ],
    },
    parent: {
      childName: "Ivaan",
      lastReflectionDate: "2025-12-22",
      commonBlocks: { "Fear of Mistakes": 60, "Peer Pressure": 25, "Blank Mind": 15 },
      microStepsCompleted: 7,
      suggestedActions: [
        "Praise micro-step completion",
        "Ask gentle question: 'What did you think about today?'",
        "Encourage private reflection",
      ],
    },
    teacher: {
      className: "6A",
      totalStudents: 30,
      blocksDistribution: { "Fear of Mistakes": 50, "Peer Pressure": 30, "Blank Mind": 20 },
      expressionModes: { Writing: 40, Voice: 35, Drawing: 25 },
      studentInsights: [
        { name: "Ivaan", block: "Fear of Mistakes", suggestedAction: "Allow written answer first" },
        { name: "Diya", block: "Peer Pressure", suggestedAction: "Pair with supportive friend" },
      ],
    },
  };

  const tabs = {
    child: ["Home", "Reflection", "Micro-Steps", "Progress", "Settings"],
    parent: ["Dashboard", "Trends", "Suggested Actions", "Notifications", "Settings"],
    teacher: ["Class Overview", "Student Insights", "Classroom Trends", "Suggested Actions", "Notes"],
  };

  const parentChartData = {
    labels: Object.keys(dummyData.parent.commonBlocks),
    datasets: [
      {
        label: "Common Blocks (%)",
        data: Object.values(dummyData.parent.commonBlocks),
        backgroundColor: ["#22c55e", "#14b8a6", "#38bdf8"],
      },
    ],
  };

  const teacherChartData = {
    labels: Object.keys(dummyData.teacher.blocksDistribution),
    datasets: [
      {
        label: "Student Blocks (%)",
        data: Object.values(dummyData.teacher.blocksDistribution),
        backgroundColor: ["#f43f5e", "#facc15", "#3b82f6"],
      },
    ],
  };

  // -------------------- Gamification Handlers --------------------
  const handleMicroStepSelect = (step) => {
    setSelectedMicroStep(step);
    setTreeLeaves((prev) => prev + 1);
    setStreakDays((prev) => prev + 1);
    if (!badges.includes("Micro-Step Achiever")) {
      setBadges([...badges, "Micro-Step Achiever"]);
      setNewBadge("Micro-Step Achiever");
      setTimeout(() => setNewBadge(""), 3000); 
    }
  };

  // -------------------- Render Child Tab Content --------------------
  const renderTabContent = () => {
    if (stakeholder === "child") {
      switch (activeTab.toLowerCase()) {
        case "home":
          return (
            <div className="p-4">
              <h2 className="text-lg lg:text-xl font-bold mb-2">🌱 Welcome, {dummyData.child.name}</h2>
              <p className="mb-4 text-teal-400">
                {reflectionText || "How are you feeling today? Express yourself!"}
              </p>

              <div className="grid grid-cols-3 gap-4 mb-4 place-items-center">
                <button
                  className="bg-teal-600 p-4 rounded text-white flex flex-col items-center hover:bg-teal-500"
                  onClick={() => setActiveMode(activeMode === "write" ? "" : "write")}
                >
                  <FontAwesomeIcon icon={faPen} size="2x" />
                  Write
                </button>

                <button
                  className="bg-teal-600 p-4 rounded text-white flex flex-col items-center hover:bg-teal-500"
                  onClick={() => setActiveMode(activeMode === "voice" ? "" : "voice")}
                >
                  <FontAwesomeIcon icon={faMicrophone} size="2x" />
                  Voice
                </button>

                <button
                  className="bg-teal-600 p-4 rounded text-white flex flex-col items-center hover:bg-teal-500"
                  onClick={() => setActiveMode(activeMode === "draw" ? "" : "draw")}
                >
                  <FontAwesomeIcon icon={faPalette} size="2x" />
                  Draw
                </button>
              </div>

              {/* Interactive Panel */}
              <AnimatePresence>
                {activeMode && (
                  <motion.div
                    key={activeMode}
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="p-4 mb-4 border border-teal-600 rounded bg-gray-900"
                  >
                    {activeMode === "write" && (
                      <div className="flex flex-col gap-2">
                        <textarea
                          placeholder="Write your thoughts here..."
                          rows={4}
                          className="w-full p-2 rounded bg-gray-800 text-white border border-teal-600"
                        />
                        <button
                          className="bg-teal-600 px-4 py-2 rounded hover:bg-teal-500"
                          onClick={() => handleCompleteAction("write")}
                        >
                          Submit Writing
                        </button>
                      </div>
                    )}

                    {activeMode === "voice" && (
                      <div className="flex flex-col gap-2">
                        <p>🎤 Simulated voice recording…</p>
                        <button
                          className="bg-teal-600 px-4 py-2 rounded hover:bg-teal-500"
                          onClick={() => handleCompleteAction("voice")}
                        >
                          Finish Recording
                        </button>
                      </div>
                    )}

                    {activeMode === "draw" && (
                      <div className="flex flex-col gap-2">
                        <p>🎨 Simulated drawing canvas…</p>
                        <button
                          className="bg-teal-600 px-4 py-2 rounded hover:bg-teal-500"
                          onClick={() => handleCompleteAction("draw")}
                        >
                          Submit Drawing
                        </button>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Gamification Display */}
              <div className="flex items-center gap-4 mb-4">
                <div className="flex items-center">
                  <FontAwesomeIcon icon={faLeaf} size="2x" className="text-green-400 mr-2" />
                  Tree Leaves: {treeLeaves}
                </div>
                <div className="flex items-center">
                  <FontAwesomeIcon icon={faStar} size="2x" className="text-yellow-400 mr-2" />
                  Streak Days: {streakDays}
                </div>
              </div>

              <p className="mb-2">Badges:</p>
              <ul className="list-disc pl-5 mb-4">
                {badges.map((b, idx) => (
                  <li key={idx}>{b}</li>
                ))}
              </ul>

              {/* Animated new badge */}
              <AnimatePresence>
                {newBadge && (
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1.5, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    transition={{ duration: 0.7 }}
                    className="absolute top-20 right-10 bg-yellow-400 text-black px-4 py-2 rounded shadow-lg"
                  >
                    🎉 New Badge: {newBadge}!
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
        );


        case "reflection":
          return (
            <div className="p-4">
              <h2 className="text-lg lg:text-xl font-bold mb-2">📝 Daily Reflection</h2>
              <textarea
                value={reflectionText}
                onChange={(e) => setReflectionText(e.target.value)}
                rows={5}
                className="w-full p-2 rounded bg-gray-800 text-white border border-teal-600"
              />
              <button
                onClick={() => alert("Reflection saved!")}
                className="mt-2 bg-teal-600 px-4 py-2 rounded hover:bg-teal-500"
              >
                Save Reflection
              </button>
            </div>
          );

        case "micro-steps":
          return (
            <div className="p-4">
              <h2 className="text-lg lg:text-xl font-bold mb-2">🚶 Micro-Steps</h2>
              <ul className="list-disc pl-5 text-white mb-4">
                {dummyData.child.microSteps.map((step, idx) => (
                  <li
                    key={idx}
                    className="mb-2 cursor-pointer hover:text-teal-400"
                    onClick={() => handleMicroStepSelect(step)}
                  >
                    {step} {selectedMicroStep === step && <span className="text-yellow-400">★ Selected</span>}
                  </li>
                ))}
              </ul>
              <p>Selected Micro-Step: <strong>{selectedMicroStep || "None"}</strong></p>
            </div>
          );

        case "progress":
          return (
            <div className="p-4">
              <h2 className="text-lg lg:text-xl font-bold mb-2">🌿 Your Progress</h2>

              {/* Tree Animation */}
              <div className="flex items-center mb-4">
                <FontAwesomeIcon icon={faLeaf} size="2x" className="text-green-400 mr-2" />
                <motion.div
                  key={treeLeaves}
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  Tree Leaves: {treeLeaves}
                </motion.div>
              </div>

              {/* Streak */}
              <div className="flex items-center mb-4">
                <FontAwesomeIcon icon={faStar} size="2x" className="text-yellow-400 mr-2" />
                <motion.div
                  key={streakDays}
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  Streak Days: {streakDays}
                </motion.div>
              </div>

              {/* Badges */}
              <p className="mb-2">Badges:</p>
              <ul className="list-disc pl-5">
                {badges.map((b, idx) => (
                  <li key={idx}>{b}</li>
                ))}
              </ul>

              {/* Animated new badge */}
              <AnimatePresence>
                {newBadge && (
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1.5, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    transition={{ duration: 0.7 }}
                    className="absolute top-20 right-10 bg-yellow-400 text-black px-4 py-2 rounded shadow-lg"
                  >
                    🎉 New Badge: {newBadge}!
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );

        case "settings":
          return (
            <div className="p-4">
              <h2 className="text-lg lg:text-xl font-bold mb-2">⚙️ Settings</h2>
              <div className="flex flex-col gap-2">
                {Object.keys(settings).map((key) => (
                  <label key={key} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={settings[key]}
                      onChange={() =>
                        setSettings((prev) => ({ ...prev, [key]: !prev[key] }))
                      }
                    />
                    {key.charAt(0).toUpperCase() + key.slice(1)}
                  </label>
                ))}
              </div>
            </div>
          );

        default:
          return <p className="p-4">Select a tab to continue</p>;
      }
    }

    // Parent & Teacher tabs are same as previous skeleton
    if (stakeholder === "parent") {
      switch (activeTab.toLowerCase()) {
        case "dashboard":
          return (
            <div className="p-4">
              <h2 className="text-lg lg:text-xl font-bold mb-2">🤝 Child Overview</h2>
              <p>Name: {dummyData.parent.childName}</p>
              <p>Last Reflection: {dummyData.parent.lastReflectionDate}</p>
            </div>
          );
        case "trends":
          return (
            <div className="w-full max-w-xs mx-auto p-8" style={{ height: 300 }}> 
              <Bar 
                data={parentChartData} 
                options={{ maintainAspectRatio: false }} 
              />
            </div>
          );
        case "suggested actions":
          return (
            <div className="p-4">
              <h2 className="text-lg lg:text-xl font-bold mb-2">💡 Suggested Actions</h2>
              <ul className="list-disc pl-5 text-white">
                {dummyData.parent.suggestedActions.map((act, idx) => (
                  <li key={idx} className="mb-2">{act}</li>
                ))}
              </ul>
            </div>
          );
        case "notifications":
          return (
            <div className="p-4">
              <h2 className="text-lg lg:text-xl font-bold mb-2">🔔 Notifications</h2>
              <p>No new notifications</p>
            </div>
          );
        default:
          return null;
      }
    }
    if (stakeholder === "teacher") {
      switch (activeTab.toLowerCase()) {
        case "class overview":
          return (
            <div className="p-4">
              <h2 className="text-lg lg:text-xl font-bold mb-2">📚 Class Overview</h2>
              <p>Class: {dummyData.teacher.className}</p>
              <p>Total Students: {dummyData.teacher.totalStudents}</p>
            </div>
          );
        case "student insights":
          return (
            <div className="p-4">
              <h2 className="text-lg lg:text-xl font-bold mb-2">👩‍🏫 Student Insights</h2>
              {dummyData.teacher.studentInsights.map((s, idx) => (
                <div key={idx} className="mb-2 p-2 border rounded border-teal-600">
                  <p>Name: {s.name}</p>
                  <p>Block: {s.block}</p>
                  <p>Suggested Action: {s.suggestedAction}</p>
                </div>
              ))}
            </div>
          );
        case "classroom trends":
          return (
            <div className="w-40 p-2"> 
              <h2 className="text-base lg:text-lg font-bold mb-1">📊 Classroom Trends</h2>
              <div style={{ height: 150 }}> 
                <Pie 
                  data={pareteacherChartDatantChartData} 
                  options={{ maintainAspectRatio: false }} 
                />
              </div>
            </div>

          );
        case "suggested actions":
          return (
            <div className="p-4">
              <h2 className="text-lg lg:text-xl font-bold mb-2">💡 Suggested Classroom Actions</h2>
              <ul className="list-disc pl-5 text-white">
                <li>Allow written answers before oral participation</li>
                <li>Pair hesitant students with supportive peers</li>
                <li>Celebrate attempts, not correctness</li>
              </ul>
            </div>
          );
        case "notes":
          return (
            <div className="p-4">
              <h2 className="text-lg lg:text-xl font-bold mb-2">📝 Teacher Notes</h2>
              <p>No notes added yet.</p>
            </div>
          );
        default:
          return null;
      }
    }
  };

  return (
    <div className="bg-black min-h-screen text-white font-sans relative">
      <header className="sticky top-0 z-50 p-4 bg-black border-b border-blue-400 flex justify-between items-center">
        <h1 className="text-xl lg:text-2xl font-bold text-teal-400">🦁 NoFearNShy</h1>
        <select
          className="bg-black text-white border border-teal-600 rounded px-2 py-1"
          value={stakeholder}
          onChange={(e) => {
            setStakeholder(e.target.value);
            setActiveTab(tabs[e.target.value][0].toLowerCase());
          }}
        >
          <option value="child">Child</option>
          <option value="parent">Parent</option>
          <option value="teacher">Teacher</option>
        </select>
      </header>
      <div className="grid grid-cols-3 lg:grid-cols-5 gap-2 border-b border-teal-600 place-items-center p-2">
        {tabs[stakeholder].map((tab) => (
          <button
            key={tab}
            className={`px-4 py-2 text-sm lg:text-[16px] whitespace-nowrap ${
              activeTab === tab.toLowerCase()
                ? "border-b-2 bg-teal-500 border-teal-400 font-bold"
                : "text-gray-400"
            }`}
            onClick={() => setActiveTab(tab.toLowerCase())}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Content */}
      <main className="p-2 flex-1">{renderTabContent()}</main>
      <footer className="bg-black border-t border-blue-400 text-white py-4">
        <div className="max-w-6xl mx-auto px-3 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="text-sm text-yellow-200">© 2025 Think5Whys Global DeepTech Solutions.</div>
          <div className="flex flex-wrap gap-4 text-sm">
            <Link to="/about-us" className="hover:text-teal-300">About</Link>
            <Link to="/privacy-policy" className="hover:text-teal-300">Privacy Policy</Link>
            <Link to="/terms-of-use" className="hover:text-teal-300">Terms of Use</Link>
            <Link to="/contact-us" className="hover:text-teal-300">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default NoFearNShyHome;
